import React, { useState } from 'react';
import { apiClient } from '../../api/client';

export interface ScheduleEvent {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venueName: string;
  venueAddress?: string;
  rsvpStatus?: string | null;
}

export interface SchedulePanelProps {
  events: ScheduleEvent[];
}

function isPastEvent(startTime: string): boolean {
  return new Date(startTime) < new Date();
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  // Parse date portion directly (stored as Eastern, no conversion needed)
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day); // Local date, no UTC shift
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(dateTimeStr: string): string {
  if (!dateTimeStr) return '';
  // Extract HH:MM from stored value (stored as Eastern, no conversion needed)
  let hours: number;
  let minutes: number;

  if (dateTimeStr.includes('T')) {
    const timePart = dateTimeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (dateTimeStr.includes(':')) {
    [hours, minutes] = dateTimeStr.split(':').map(Number);
  } else {
    return dateTimeStr;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  heading: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '0.75rem',
  },
  empty: {
    fontSize: '0.85rem',
    color: '#666',
    textAlign: 'center' as const,
    padding: '1rem 0',
  },
  eventItem: {
    padding: '0.75rem',
    borderBottom: '1px solid #f0f0f0',
  },
  eventItemLast: {
    padding: '0.75rem',
    borderBottom: 'none',
  },
  eventName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '0.25rem',
  },
  eventDetails: {
    fontSize: '0.8rem',
    color: '#555',
    lineHeight: 1.4,
  },
  venue: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.15rem',
  },
  venueAddress: {
    fontSize: '0.75rem',
    color: '#888',
    marginTop: '0.1rem',
  },
  directionsLink: {
    fontSize: '0.75rem',
    color: '#1a73e8',
    textDecoration: 'none',
    marginTop: '0.2rem',
    display: 'inline-block',
  },
  rsvpBtn: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    minHeight: '28px',
    flexShrink: 0,
    transition: 'all 0.15s',
  },
};

export function SchedulePanel({ events }: SchedulePanelProps) {
  const [rsvps, setRsvps] = useState<Record<string, string | null>>(() => {
    const map: Record<string, string | null> = {};
    events.forEach(e => { map[e.id] = e.rsvpStatus || null; });
    return map;
  });

  const toggleRsvp = async (eventId: string) => {
    const current = rsvps[eventId];
    const newStatus = current === 'attending' ? 'not_attending' : 'attending';
    setRsvps(prev => ({ ...prev, [eventId]: newStatus }));
    try {
      await apiClient(`/events/${eventId}/event-rsvp`, {
        method: 'POST',
        body: { status: newStatus },
      });
    } catch {
      // Revert on failure
      setRsvps(prev => ({ ...prev, [eventId]: current }));
    }
  };

  if (events.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.heading}>My Schedule</div>
        <div style={styles.empty}>No events scheduled.</div>
      </div>
    );
  }

  const sortedEvents = [...events].sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });

  return (
    <div style={styles.container}>
      <div style={styles.heading}>My Schedule</div>
      {sortedEvents.map((event, idx) => {
        const past = isPastEvent(event.startTime);
        const isLast = idx === sortedEvents.length - 1;
        const status = rsvps[event.id];
        const isGoing = status === 'attending';

        return (
          <div
            key={event.id}
            style={{
              ...(isLast ? styles.eventItemLast : styles.eventItem),
              opacity: past ? 0.5 : 1,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={styles.eventName}>{event.name}</div>
                <div style={styles.eventDetails}>
                  {formatDate(event.date)} &middot; {formatTime(event.startTime)} –{' '}
                  {formatTime(event.endTime)}
                </div>
              </div>
              {!past && (
                <button
                  onClick={() => toggleRsvp(event.id)}
                  style={{
                    ...styles.rsvpBtn,
                    backgroundColor: isGoing ? '#e8f5e9' : '#f5f5f5',
                    color: isGoing ? '#2e7d32' : '#666',
                    borderColor: isGoing ? '#a5d6a7' : '#e0e0e0',
                  }}
                  aria-label={isGoing ? 'Mark as not going' : 'Mark as going'}
                >
                  {isGoing ? '✓ Going' : 'Going?'}
                </button>
              )}
            </div>
            <div style={styles.venue}>📍 {event.venueName}</div>
            {event.venueAddress && (
              <>
                <div style={styles.venueAddress}>{event.venueAddress}</div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.venueAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.directionsLink}
                >
                  🗺️ Get Directions
                </a>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
